import { Observable } from "rxjs";
import { api, handleError, retryStrategy } from "../../shared/api-loaders";
import {
  HttpClient,
  HttpParams,
  HttpHeaders,
  HttpRequest,
} from "@angular/common/http";

import { Injectable } from "@angular/core";
import { McmdRole, SignOnPolicy, User } from "../models/user.model";
import { Select2Option } from "../../shared/select2-option";
import { Region } from "../models/user-detail-options";
import { IUserRegion } from "../models/user-region.model";
import * as _ from "lodash";
import { DeepLink } from "../models/DeepLink";
import { timer, of, from } from "rxjs";
import {
  switchMap,
  retryWhen,
  map,
  filter,
  concatMap,
  mergeMap,
  catchError,
  flatMap,
} from "rxjs/operators";
import { UserActivateWithPasswordComponent } from "../user-activate-with-password/user-activate-with-password.component";
import { NgbModal, NgbModalOptions } from "@ng-bootstrap/ng-bootstrap";
import { SecurityService } from "../../shared/security.service";
import { PagingInfo } from "../models/PagingInfo";
import { environment } from "../../../environments/environment";
import { UserExportRequest } from "../models/UserExportRequest";
import { PasswordCredential } from "../models/PasswordCredential";
import { UserRemoveDeactivationDateComponent } from "../user-remove-deactivation-date/user-remove-deactivation-date.component";

@Injectable()
export class UserService {
  exportUsers(filters: UserExportRequest) {
    filters.status = filters.status == "All" ? "" : filters.status;

    var headers = new HttpHeaders();
    headers = headers.append("Accept", "text/csv");

    let params = new HttpParams({ fromObject: <any>filters });
    //params = params.set("searchString", searchText);
    //params = params.set("pageNumber", pageNumber.toString());
    //params = params.set("rowsPerPage", pageSize.toString());
    //params = params.set("sortColumn", sortColumnName);
    //params = params.set("sortDirection", sortDirection);
    const req = new HttpRequest("GET", api.userManagementExportUsers(), {
      headers: headers,
      responseType: "blob",
      observe: "response",
      reportProgress: true,
      params: params,
    });
    return this._http.request(req);
  }

  constructor(
    private _http: HttpClient,
    private modalService: NgbModal,
    private secSvc: SecurityService
  ) {}

  convertToIDM(ssoid: string, password: string) {
    return this._http
      .post<any>(api.convertToIDM(ssoid), new PasswordCredential(password))
      .pipe(retryWhen(retryStrategy()));
  }
  convertToFederated(ssoid: string, userFederatedId: string) {
    let params = new HttpParams();
    if (userFederatedId) {
      params = params.set("federatedLoginId", userFederatedId);
    }

    return this._http
      .post<any>(api.convertToFederated(ssoid), {}, { params: params })
      .pipe(retryWhen(retryStrategy()));
  }
  resetMyFactors() {
    return this._http
      .post<any>(api.resetMyFactors(), {})
      .pipe(retryWhen(retryStrategy()));
  }
  resetFactors(ssoId: string) {
    return this._http
      .post<any>(api.resetFactors(ssoId), {})
      .pipe(retryWhen(retryStrategy()));
  }

  updateFactors(ssoId: string, enabled: boolean) {
    return this._http
      .post<any>(api.updateFactors(ssoId), { mfaEnabled: enabled })
      .pipe(retryWhen(retryStrategy()));
  }

  disableFactor(id: any): any {
    return timer(0).pipe(
      switchMap((x) => {
        return this._http.delete(api.disableFactor(id));
      }),
      retryWhen(retryStrategy())
    );
  }

  activateFactor(id: any, params: HttpParams): any {
    return timer(0).pipe(
      switchMap((x) => {
        return this._http.post(api.activateFactor(id), null, {
          params: params,
        });
      }),
      retryWhen(retryStrategy())
    );
  }

  setFactor(id: string, params: HttpParams): any {
    return timer(0).pipe(
      switchMap((x) => {
        return this._http.post(api.factor(id), null, { params: params });
      }),
      retryWhen(retryStrategy())
    );
  }

  getPage(
    searchText: string,
    sortColumnName: string,
    sortDirection: string,
    pageNumber: number,
    pageSize: any,
    userStatus: string,
    includePhysicians: boolean = false,
    location: string = ''
  ) {
    let params = new HttpParams();
    params = params.set("searchString", searchText);
    params = params.set("pageNumber", pageNumber.toString());
    params = params.set("rowsPerPage", pageSize.toString());
    params = params.set("sortColumn", sortColumnName);
    params = params.set("sortDirection", sortDirection);
    if (userStatus.trim()) {
      params = params.set("userStatus", userStatus);
    }
    if (includePhysicians) {
      params = params.set("includePhysicians", "true");
    }
    if (location) {
      params = params.set("location", location);
    }

    return timer(0).pipe(
      switchMap((x) =>
        this._http.get<PagingInfo<User>>(api.users(null, null), {
          params: params,
        })
      ),
      retryWhen(retryStrategy())
    );
  }

  getStatus() {
    return timer(0).pipe(
      switchMap((x) => this._http.get<any[]>(api.usersStatus())),
      retryWhen(retryStrategy())
    );
  }

  getUsers(params: HttpParams): Observable<any> {
    return timer(0).pipe(
      switchMap((x) => {
        return this._http.get(api.users(), {
          observe: "response",
          params: params,
        });
      }),
      map((data) => {
        return { data: data.body, headers: data.headers };
      }),
      retryWhen(retryStrategy())
    );
  }

  getNextUsers(headerParam: string): Observable<any> {
    var headers = new HttpHeaders();
    headers = headers.append("Mc-Next-Link", headerParam);

    return timer(0).pipe(
      switchMap((x) => {
        return this._http.get(api.users(), {
          observe: "response",
          headers: headers,
        });
      }),
      map((data) => {
        return { data: data.body, headers: data.headers };
      }),
      retryWhen(retryStrategy())
    );
  }

  getUser(id: string): Observable<User> {
    return timer(0).pipe(
      switchMap((x) => this._http.get<User>(api.user(id))),
      retryWhen(retryStrategy())
    );
  }

  getDeepLinks(configId: string): Observable<DeepLink[]> {
    return timer(0).pipe(
      switchMap((x) => {
        return this._http.get<DeepLink[]>(api.deepLinks(configId));
      }),
      retryWhen(retryStrategy())
    );
  }

  create(user: User, provisionSL: boolean): Observable<User> {
    return timer(0).pipe(
      switchMap((x) => this._http.post<User>(api.users(provisionSL), user)),
      retryWhen(retryStrategy())
    );
  }

  updateUser(user: User, provisionSL: boolean): Observable<User> {
    return timer(0).pipe(
      switchMap((x) => this._http.put<User>(api.users(provisionSL), user)),
      retryWhen(retryStrategy())
    );
  }

  suspend(id: string): any {
    return timer(0).pipe(
      switchMap((x) => {
        return this._http.post(api.suspend(id), null);
      }),
      retryWhen(retryStrategy())
    );
  }

  async activate(user: User, status: string): Promise<any> {
    //scenario where passord prompt is not required
    //1. If you have an email
    //2. If you are coming from a Suspend state
    //3. If you are federated and there is no email

    let hasEmail = !this.isDummyEmail(user.email);
    let isFederated = user.federatedIdentity;

    if (hasEmail || status == "Suspended" || (isFederated && !hasEmail)) {
      // if deactivation date separate path
      if (user.deactivationDate) {
        return this.showRemoveDeactivationDateModal(user);
      } else {
        let result = await timer(0)
          .pipe(
            switchMap((x) => {
              return this._http.post(api.activate(user.ssoid), null);
            }),
            retryWhen(retryStrategy())
          )
          .toPromise();

        // Set usesMatrixcare to true for Physician/NPPA users after successful activation
        if (user.physicianNppaEnabled) {
          user.usesMatrixcare = true;
          await this.updateUser(
            user,
            environment.enableSLAutoProvisioning.toLowerCase() === "true"
          ).toPromise();
        }

        return {};
      }
    }

    const options: NgbModalOptions = {
      size: "lg",
      backdrop: "static",
    };

    let ref = this.modalService.open(
      UserActivateWithPasswordComponent,
      options
    );
    let f = <UserActivateWithPasswordComponent>ref.componentInstance;
    f.firstName = user.firstName;
    f.lastName = user.lastName;
    f.userName = user.userName;
    if (user.deactivationDate) {
      f.deactivationDate = user.deactivationDate;
    }

    let newPassword = "";
    try {
      newPassword = await ref.result;
    } catch (e) {}
    if (!newPassword) return null;

    if (environment.useLegacyAPI) {
      await this._http
        .post(api.activate(user.ssoid), null)
        .pipe(retryWhen(retryStrategy()))
        .toPromise();
    }
    await this._http
      .post(api.activateWithPassword(user.ssoid), {
        credentials: { password: { value: newPassword } },
      })
      .pipe(retryWhen(retryStrategy()))
      .toPromise();

    // Set usesMatrixcare to true for Physician/NPPA users after successful activation
    if (user.physicianNppaEnabled) {
      user.usesMatrixcare = true;
      await this.updateUser(
        user,
        environment.enableSLAutoProvisioning.toLowerCase() === "true"
      ).toPromise();
    }

    return {};
  }

  deactivate(id: string): any {
    return timer(0).pipe(
      switchMap((x) => {
        return this._http.post(api.deactivate(id), null);
      }),
      retryWhen(retryStrategy())
    );
  }

  delete(id: string): any {
    return timer(0).pipe(
      switchMap((x) => {
        return this._http.delete(api.delete(id));
      }),
      retryWhen(retryStrategy())
    );
  }

  unlock(id: string): any {
    return timer(0).pipe(
      switchMap((x) => {
        return this._http.post(api.unlock(id), null);
      }),
      retryWhen(retryStrategy())
    );
  }

  expirePassword(id: string): Observable<string> {
    return timer(0).pipe(
      switchMap((x) => {
        return this._http.post<string>(api.expirePassword(id), null, {
          responseType: "text" as "json",
        });
      }),
      retryWhen(retryStrategy())
    );
  }

  resetPassword(id: string) {
    return timer(0).pipe(
      switchMap((x) => {
        return this._http.post(api.resetPassword(id), null);
      }),
      retryWhen(retryStrategy())
    );
  }

  setTempPassword(id: string, tempPassword: string) {
    return timer(0).pipe(
      switchMap((x) => {
        return this._http.post(api.setTempPassword(id), {
          credentials: { password: { value: tempPassword } },
        });
      }),
      retryWhen(retryStrategy())
    );
  }

  getUserAccessTypes() {
    return {
      RU: "No Admin Rights",
      LMA: "Location Admin",
      CLA: "Limited Admin",
      UMA: "Corporate Admin",
      LLA: "Location Limited Admin",
    };
  }

  getUserAccessTypesLongerFormat() {
    return {
      RU: "No Admin Rights",
      LMA: "Location Admin - All admin rights for single MatrixCare 360 location",
      CLA: "Limited Admin - Limited admin rights for all locations",
      UMA: "Corporate Admin - All admin rights for all locations",
      LLA: "Location Limited Admin - Limited admin rights for single MatrixCare 360 location",
    };
  }

  async getUserAccessTypesOptions(
    longerFormat: boolean = false
  ): Promise<Select2Option[]> {
    let types = longerFormat
      ? this.getUserAccessTypesLongerFormat()
      : this.getUserAccessTypes();

    let options: Select2Option[] = Object.keys(types).map((x) => {
      return { id: x, text: types[x] };
    });
    let isLocalAdmin = await this.secSvc
      .isUserManagementLocalAdmin()
      .toPromise();
    if (isLocalAdmin) return options.filter((x) => x.id == "RU");

    return options;
  }

  getNonLmaUserAccessTypes(): Select2Option[] {
    let types = {
      RU: "No Admin Rights",
      CLA: "Limited Admin - Limited admin rights for all locations",
      UMA: "Corporate Admin - All admin rights for all locations",
    };

    let options: Select2Option[] = Object.keys(types).map((x) => {
      return { id: x, text: types[x] };
    });
    return options;
  }

  getUserAccesText(id: string) {
    const notFoundText = "Not Selected";

    if (!id) return notFoundText;
    return this.getUserAccessTypes()[id] || notFoundText;
  }

  getDummyEmail(): string {
    return "noemail@matrixcare.com";
  }

  isDummyEmail(email: string) {
    if (!email) return true;

    if (!email.trim()) return true;

    if (email == this.getDummyEmail()) return true;

    return false;
  }

  setSelectedRegions(regions: Region[], userRegions: IUserRegion[]): Region[] {
    let clonedRegions = [];
    if (regions) {
      clonedRegions = _.cloneDeep(regions); //Object.assign([], regions);
      if (userRegions) {
        clonedRegions.forEach((r) => {
          let exists = userRegions.some(
            (s) => s.id === r.id && s.type === r.type
          );
          r.checked = exists;
          r.districts.forEach((d) => {
            exists = userRegions.some(
              (s) => s.id === d.id && s.type === d.type
            );
            d.checked = exists;
            d.facilities.forEach((f) => {
              exists = userRegions.some(
                (s) => s.id === f.id && s.type === f.type
              );
              f.checked = exists;
            });
          });
        });
      }
    }
    return clonedRegions;
  }

  createRDLSearchClearButton(element, renderer, treeviewComponent) {
    const filterParentElement: HTMLElement = (<HTMLElement>(
      element.nativeElement
    )).querySelector(".row-filter");
    if (filterParentElement) {
      const searchRDLElement: HTMLElement =
        filterParentElement.querySelector(".col-12");
      if (searchRDLElement) {
        renderer.removeClass(searchRDLElement, "col-12");
        renderer.addClass(searchRDLElement, "col-10");

        const clearButton = filterParentElement.querySelector(".btn-cancel");
        if (!clearButton) {
          const clearButtonElement = renderer.createElement("button");
          const text = renderer.createText("Clear");

          renderer.appendChild(clearButtonElement, text);

          renderer.addClass(clearButtonElement, "btn");
          renderer.addClass(clearButtonElement, "btn-cancel");

          renderer.listen(clearButtonElement, "click", (event: Event) => {
            event.preventDefault();
            if (treeviewComponent) {
              treeviewComponent.onFilterTextChange("");
            }
          });

          renderer.appendChild(filterParentElement, clearButtonElement);
        }
      }
    }
  }

  public getMcmdRoles(): Observable<any> {
    return timer(0).pipe(
      switchMap((x) => this._http.get<any[]>(api.getMcmdRoles())),
      retryWhen(retryStrategy())
    );
  }

  //for the create user page
  getPPRoleIdText(): Select2Option[] {
    let types = {
      FACILITYADMIN: "Facility Admin",
      FACILITYSUPERADMIN: "Facility Super Admin",
    };
    let options: Select2Option[] = Object.keys(types).map((x) => {
      return { id: x, text: types[x] };
    });

    options.unshift({ id: 'Not Selected', text: 'Not Selected' });

    return options;
  }

  //for getting PP roles (not editing)
  getPaymentPortalRolesText(id: string) {
    const notFoundText = "Not Selected";

    if (!id) return notFoundText;
    return this.getPaymentPortalRoleTypes()[id] || notFoundText;
  }

  getPaymentPortalRoleTypes() {
    return {
      FACILITYADMIN: "Facility Admin",
      FACILITYSUPERADMIN: "Facility Super Admin",
    };
  }

  //for the create user page
  getRTORoleIdText(): Select2Option[] {
    let types = {
      RTOFacilityAdmin: "Facility Admin",
      RTOSuperAdmin: "Super Admin",
    };
    let options: Select2Option[] = Object.keys(types).map((x) => {
      return { id: x, text: types[x] };
    });

    options.unshift({ id: 'Not Selected', text: 'Not Selected' });

    return options;
  }

  //for getting RTO roles (not editing)
  getRTORolesText(id: string) {
    const notFoundText = "Not Selected";

    if (!id) return notFoundText;
    return this.getRTORoleTypes()[id] || notFoundText;
  }

  getRTORoleTypes() {
    return {
      RTOFacilityAdmin: "Facility Admin",
      RTOSuperAdmin: "Super Admin",
    };
  }

  public getMcmdRole(id: number): Observable<McmdRole> {
    return timer(0).pipe(
      switchMap((x) => this._http.get<McmdRole>(api.getMcmdRoleById(id))),
      retryWhen(retryStrategy())
    );
  }

  public getSignonPolicies(): Observable<SignOnPolicy[]> {
    return timer(0).pipe(
      switchMap((x) => this._http.get<SignOnPolicy[]>(api.getSignOnPolicies())),
      retryWhen(retryStrategy())
    );
  }

  async showRemoveDeactivationDateModal(user: User) {
    const options: NgbModalOptions = {
      size: "lg",
      backdrop: "static",
    };

    let ref = this.modalService.open(
      UserRemoveDeactivationDateComponent,
      options
    );
    let f = <UserRemoveDeactivationDateComponent>ref.componentInstance;
    if (user.deactivationDate) {
      f.deactivationDate = user.deactivationDate;
    }

    let resetPassword = false;
    try {
      resetPassword = await ref.result;
    } catch (e) {}

    if (!resetPassword) return null;

    if (resetPassword) {
      let result = await timer(0)
        .pipe(
          switchMap((x) => {
            return this._http.post(api.activate(user.ssoid), null);
          }),
          retryWhen(retryStrategy())
        )
        .toPromise();

      return {};
    }

    return {};
  }
}
